import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Reveal from '../components/Reveal';
import useSeo from '../hooks/useSeo';
import { IMAGES, INSIGHTS } from '../mock';

const Insights = () => {
  useSeo({
    title: 'News & Insights | Duffy Investment Group',
    description: 'Read news, updates and insights from Duffy Investment Group covering investments, business activities, industries and global market interests.',
    keywords: 'Duffy Investment Group news, investment news, investment insights, business news, investment updates, global investments',
  });

  const items = INSIGHTS;

  return (
    <div>
      {/* ===================== HERO ===================== */}
      <section className="relative bg-navy-grad overflow-hidden">
        <div className="absolute inset-0 lg:left-[45%]">
          <img src={IMAGES.newspapers} alt="News and media" className="w-full h-full object-cover" />
          <div className="absolute inset-0 lg:hidden" style={{ background: 'linear-gradient(180deg, rgba(7,19,34,0.45) 0%, rgba(7,19,34,0.7) 45%, rgba(7,19,34,0.96) 100%)' }} />
          <div className="absolute inset-0 hidden lg:block" style={{ background: 'linear-gradient(90deg, rgba(10,26,48,1) 0%, rgba(10,26,48,0.7) 18%, rgba(10,26,48,0.1) 45%)' }} />
        </div>
        <div className="relative dig-container">
          <div className="flex items-center" style={{ minHeight: 340 }}>
            <div className="pt-10 pb-14 max-w-lg">
              <Reveal>
                <h1 className="font-serif-dig text-white" style={{ fontSize: 'clamp(1.7rem, 5.6vw, 3rem)', fontWeight: 700, lineHeight: 1.1 }}>
                  ALL NEWS
                </h1>
                <span className="gold-rule-lg block my-6" />
                <p className="font-body" style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7 }}>
                  The latest news, milestones and media coverage featuring DIG and Craig Duffy.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FILTER + LIST ===================== */}
      <section className="bg-cream">
        <div className="dig-container py-10 md:py-14">
          {/* Single unified category */}
          <div className="flex items-center" style={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
            <span
              data-testid="insights-tab-all-news"
              className="font-label"
              style={{
                fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.06em',
                padding: '0.75rem 1.1rem',
                color: '#1a1204', background: 'var(--gold)',
              }}
            >
              ALL NEWS
            </span>
          </div>

          {/* List */}
          <div className="mt-2" data-testid="insights-list">
            {items.map((n, i) => (
              <Reveal key={i} delay={Math.min(i * 40, 200)}>
                <article className="flex gap-5 py-7" style={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                  {/* Date box */}
                  {(n.day || n.month || n.year) && (
                    <div className="shrink-0 grid place-items-center text-center bg-navy" style={{ width: 62, height: 62, color: '#fff' }}>
                      {n.day && n.day !== '—' && <span className="font-serif-dig" style={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1 }}>{n.day}</span>}
                      <span className="font-label" style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.08em', marginTop: 2 }}>{n.month}</span>
                      <span className="font-label" style={{ fontSize: '0.58rem', color: 'var(--gold-light)' }}>{n.year}</span>
                    </div>
                  )}
                  {/* Body */}
                  <div className="flex-1">
                    <h3 className="font-serif-dig navy-text" style={{ fontSize: '1.2rem', fontWeight: 700, lineHeight: 1.25 }}>{n.title}</h3>
                    {n.text && <p className="font-body mt-2" style={{ color: 'var(--muted)', lineHeight: 1.6 }}>{n.text}</p>}
                    {n.extra && <p className="font-body mt-2" style={{ color: 'var(--muted)', lineHeight: 1.6, fontSize: '0.92rem' }}>{n.extra}</p>}
                    {n.links?.length > 0 && (
                      <div className="flex flex-wrap items-center gap-x-7 gap-y-2 mt-3">
                        {n.links.map((href, li) => (
                          <a
                            key={li}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`news-link-${i}-${li}`}
                            className="link-gold"
                          >
                            VIEW THE FULL STORY <ChevronRight size={14} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Thumb */}
                  {n.img && (
                    <div className="hidden sm:block shrink-0" style={{ width: 130, height: 92, overflow: 'hidden' }}>
                      {n.imgLink ? (
                        <a href={n.imgLink} target="_blank" rel="noopener noreferrer" data-testid={`news-thumb-link-${i}`}>
                          <img src={IMAGES[n.img]} alt={n.title} className="w-full h-full object-cover" />
                        </a>
                      ) : (
                        <img src={IMAGES[n.img]} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                  )}
                </article>
              </Reveal>
            ))}
            {items.length === 0 && (
              <p className="font-body py-12 text-center" style={{ color: 'var(--muted)' }}>No stories yet.</p>
            )}
          </div>
        </div>
      </section>

      {/* ===================== CTA BAND ===================== */}
      <section className="bg-navy">
        <div className="dig-container py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <h3 className="font-serif-dig gold-text" style={{ fontSize: '1.4rem', fontWeight: 700 }}>EXPERIENCE. INTEGRITY. RESULTS.</h3>
            <div className="flex flex-col sm:flex-row items-center gap-5">
              <p className="font-body text-center sm:text-right" style={{ color: 'rgba(255,255,255,0.82)' }}>
                For media enquiries or interview requests,<br className="hidden sm:block" /> please contact DIG.
              </p>
              <Link to="/contact" className="btn-outline-gold">CONTACT DIG</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Insights;
